import React from 'react';
import Icon from '../../../components/AppIcon';
import { AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const CommissionAnalytics = ({ data }) => {
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  const formatCurrency = (value) => {
    return `$${value?.toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
  };

  const formatPercentage = (value) => {
    return `${value}%`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-foreground">{`${label}`}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry?.color }}>
              {`${entry?.name}: ${formatCurrency(entry?.value)}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-foreground">{data?.territory}</p>
          <p className="text-sm text-primary">{formatCurrency(data?.earnings)}</p>
          <p className="text-sm text-muted-foreground">{formatPercentage(data?.percentage)}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Commission Analytics</h3>
        <Icon name="TrendingUp" size={20} className="text-primary" />
      </div>
      <div className="space-y-6">
        {/* Monthly Trends */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Monthly Earnings Trend</h4>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data?.monthlyTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis 
                  dataKey="month" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                  tickFormatter={formatCurrency}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="earnings"
                  stroke="hsl(var(--primary))"
                  fill="hsl(var(--primary))"
                  fillOpacity={0.1}
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Territory Performance */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Territory Breakdown</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Pie Chart */}
            <div className="h-32">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data?.territoryBreakdown}
                    cx="50%"
                    cy="50%"
                    innerRadius={20}
                    outerRadius={60}
                    paddingAngle={2}
                    dataKey="percentage"
                  >
                    {data?.territoryBreakdown?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            {/* Legend */}
            <div className="space-y-2">
              {data?.territoryBreakdown?.map((territory, index) => (
                <div key={territory?.territory} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: COLORS?.[index % COLORS?.length] }}
                    />
                    <span className="text-sm text-foreground">{territory?.territory}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-foreground">
                      {formatCurrency(territory?.earnings)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {territory?.percentage}%
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Performing Transaction Types */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Transaction Type Performance</h4>
          <div className="space-y-3">
            {data?.topPerformingTransactionTypes?.map((type, index) => (
              <div key={type?.type} className="bg-background border border-border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">{type?.type}</span>
                  <span className="text-sm font-semibold text-primary">
                    {formatCurrency(type?.earnings)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{type?.count} transactions</span>
                  <span>Avg: {formatCurrency(type?.earnings / type?.count)}</span>
                </div>
                {/* Progress Bar */}
                <div className="mt-2 w-full bg-muted rounded-full h-1.5">
                  <div
                    className="bg-primary h-1.5 rounded-full transition-all duration-300"
                    style={{
                      width: `${(type?.earnings / Math.max(...data?.topPerformingTransactionTypes?.map(t => t?.earnings))) * 100}%`
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-background border border-border rounded-lg p-3 text-center">
            <Icon name="Target" size={16} className="text-primary mx-auto mb-1" />
            <p className="text-xs text-muted-foreground">Avg Commission</p>
            <p className="text-sm font-semibold text-foreground">$185.50</p>
          </div>
          <div className="bg-background border border-border rounded-lg p-3 text-center">
            <Icon name="Calendar" size={16} className="text-success mx-auto mb-1" />
            <p className="text-xs text-muted-foreground">Growth Rate</p>
            <p className="text-sm font-semibold text-success">+22.3%</p>
          </div>
        </div>

        {/* Performance Insights */}
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Lightbulb" size={16} className="text-primary" />
            <h5 className="text-sm font-medium text-primary">Performance Insights</h5>
          </div>
          <ul className="text-xs text-muted-foreground space-y-1">
            <li>• International transfers generate 66% of your commission income</li>
            <li>• North America territory shows strongest growth (+28% this month)</li>
            <li>• You're on track to reach the next commission tier this quarter</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CommissionAnalytics;